//  ----------------------------------------------------------------------------
//  Dependencies
//  ----------------------------------------------------------------------------
import dayjs from "dayjs";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Check, Close, KeyboardArrowDown } from "@mui/icons-material";
import { Modal } from "@mui/material";
import { Dropdown, DatePicker } from "antd";
import RetakeModal from "components/RetakeModal";
import { useAlert } from "components/UI/alertSakeBar/alertProvider";
import { useAssignmentCoverTitle } from "components/hooks/pageCover/useAssignmentCoverTitle";
import { find, get, has } from "lodash";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useBookUnits } from "store/booksUnits/selectors";
import { useCurrentClass } from "store/classes/selectors";
import { useExams } from "store/exams/selectors";
import { useOrgTimezone } from "store/organizations/selectors";
import { handleCreatePastSubmission } from "store/pastSubmission/thunks";
import { useResources } from "store/resources/selectors";
import { useServertime } from "store/servertime/selectors";
import { useStory } from "store/story/selectors";
import { useUserClasses } from "store/userClasses/selectors";
import { useCurrentUser } from "store/users/selectors";
import styled from "styled-components";
import {
  nothingToSee,
} from "utils/assets/images";
import ArrowLeftSvg from "../../utils/assets/images/ArrowLeft.svg";
import ArrowRightSvg from "../../utils/assets/images/ArrowRight.svg";
import eyeIconSvg from "../../utils/assets/images/eyeIcon.svg";
import dotsSvg from "../../utils/assets/images/dots.svg";
import completeGradebookSvg from "../../utils/assets/images/complete_gradebook.svg";
import { BACK_TO_BOOK, BookSynoname } from "utils/config/reference";
import COLOR from "utils/constants/color";
import {
  generateUrlForNonEbook,
  getAssignmentNavigationUrl,
  getEbookAssignmentDeepLinkUrl,
  getStoryUrl,
  imageFetch,
  imageFetchFromPodcast,
  isVideo
} from "utils/lib/ebookHelper";
import {
  checkIsManualGradable,
  isAfterTime,
  isBeforTime,
} from "utils/methods/math";
import AssignmentResponse from "./AssignmentResponse";
import AssignmentTitle from "./AssignmentTitle";
import { getCoverForAssignment, isWaitingForAiGradingRestriction } from "utils/methods/functions";
import { useUserProfileConfig } from 'store/users/selectors';
import { usePageCover } from "components/hooks/pageCover/pageCover";
import SentryWrap from 'components/Sentry';
import { handleListQuestionOfWorksheet } from "store/worksheet/thunks";
import { handleSubmitWorksheetAssignment } from "store/activities/thunks";
import { handleListUserSubmissionsByClass } from "store/submissions/thunks";
import { setWorksheetView } from "store/worksheetView/action";
import { useClassGameActivity } from "store/one_game/selectors";
import * as Sentry from "@sentry/react";

//  ----------------------------------------------------------------------------
//  Styles
//  ----------------------------------------------------------------------------

const ImageModal = styled.div`
  left: 50%;
  max-width: 544px;
  padding: 0px 24px;
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 100%;
`;

const Image = styled.img`
  border: 1px solid ${COLOR.black800};
  width: 100%;
  height: auto;
`;

const NoAssignmentImg = styled.img`
  height: 312px;
  width: initial;
  object-fit: initial;
`;

const CompleteText = styled.span`
  align-items: center;
  color: ${(p) => (p.deactivate ? COLOR.redishpink : "#6BB72F")};
  display: flex;
  flex-direction: row;
  font-size: 14px;
  font-weight: 600;
`;

export default function StudentGradebook({
  filteredAssignments,
  booksAllList,
  sectionSort,
  fetchAssignmentSubmission = () => {},
  viewType,
}) {
  const showAlert = useAlert();
  const story = useStory();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const bookUnits = useBookUnits();
  const resources = useResources();
  const servertime = useServertime();
  const timezone = useOrgTimezone();
  const currentClass = useCurrentClass();
  const userClasses = useUserClasses();
  const currentUser = useCurrentUser();
  const exams = useExams();
  const { isAiPermissionEnabled } = useUserProfileConfig();
  const { class_game_activity } = useClassGameActivity();
  const [currentData, setCurrentData] = useState({});
  const [modalOpen, setModalState] = useState(false);
  const [groupedByDate, setGroupedByDate] = useState({});
  const [isRetakeOpen, setisRetakeOpen] = useState(false);
  const [retaksAvailable, setRetakesAvailable] = useState();
  const [responsesModalOpen, setResponsesModalOpen] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(dayjs().startOf("month"));
  const [dateRangeMode, setDateRangeMode] = useState("semester");
  const [customStartDate, setCustomStartDate] = useState(null);
  const [customEndDate, setCustomEndDate] = useState(null);
  const [dateDropdownOpen, setDateDropdownOpen] = useState(false);

  const coverTitle = useAssignmentCoverTitle({ assignment: currentData });

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (dateRangeMode !== "current_month") return;
      if (e.key === "ArrowLeft") {
        goToPreviousMonth();
      } else if (e.key === "ArrowRight") {
        goToNextMonth();
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [dateRangeMode]);
  const pageCover = usePageCover({ assignmentData: currentData?.data });
  const getThumbnail = (zoom) => {
    const src = isVideo(get(currentData, "data.chapter"), resources)
      ? imageFetchFromPodcast(get(currentData, "data.page"), resources)
      : imageFetch(
        booksAllList,
        get(currentData, "data.chapter"),
        get(currentData, "data.page")
      );
    const cover_img_ext = getCoverForAssignment(currentData)
    const cover_img = pageCover ? pageCover : cover_img_ext ? cover_img_ext : src;
    return (
      <>
        <Image src={cover_img} alt="" />
        {/* {modalOpen && (
          <ImageStoryTitle>
            {currentData?.data?.displayName || ""}
          </ImageStoryTitle>
        )} */}
      </>
    );
  };

  const view_result = useMemo(() => {
    const result_visibility_on = currentData?.result_visibility_on;
    return result_visibility_on ? isBeforTime(result_visibility_on) : true
  }, [currentData])

  const generateUrl = (CHAPTER_ID, page, activity, quiz) => {
    const storyUrl = getStoryUrl(story, CHAPTER_ID, page, quiz, activity);
    const backToLabel = BACK_TO_BOOK[CHAPTER_ID];
    const BALCON_PAGES = get(
      find(bookUnits, ["bookId", CHAPTER_ID]),
      "pages",
      []
    );
    const bookitem = find(
      BALCON_PAGES,
      (k) => parseInt(k.start) <= page && parseInt(k.finish) >= page
    );
    const url = generateUrlForNonEbook(
      bookitem?.id,
      page,
      activity,
      quiz,
      backToLabel,
      null,
      CHAPTER_ID
    );
    if (url && backToLabel) {
      return url;
    } else if (storyUrl) {
      return storyUrl;
    } else {
      return null;
    }
  };
  const goToBook = (chapter, pageId) => {
    const generatedUrl = generateUrl(chapter, pageId, null, null);
    if (isVideo(chapter, resources)) {
      navigate(`/video?id=${chapter}&quiz=null`);
    } else if (generatedUrl) {
      navigate(generatedUrl);
    } else {
      navigate(
        `/ebook?chapter=${chapter}&page=${pageId}&activity=null&quiz=null`
      );
    }
  };

  const isWaitingForAiGrading = () => {
    const is_waiting = isWaitingForAiGradingRestriction(currentData?.submission,currentData ,isAiPermissionEnabled);
    if (is_waiting) {
      showAlert('Please wait while the AI processes your grading.', 'warning');
      return true;
    }
    return false;
  };

  const isExamHidden = useCallback((chapter) => {
    const fetchExams = exams?.find((entry) =>
      entry.exams.flatMap((exam) => `${exam.bookId}`).includes(`${chapter}`)
    );
    if (!fetchExams) {
      return false;
    }
    const classInfo = find(userClasses, ["id", currentClass?.id]);
    const isHidden = classInfo?.config?.meta_data || {};
    const examLevel = fetchExams?.exam_school || "";
    if (isHidden.highSchool && examLevel === "high") {
      return true;
    }
    if (isHidden.middleSchool && examLevel === "middle") {
      return true;
    }
    return false;
  }, [exams, userClasses, currentClass?.id]);

  const checkRetake = () => {
    if (isWaitingForAiGrading()) return;
    if (getButtonText(currentData?.submission, currentData) === "Retake") {
      setisRetakeOpen(true);
    } else {
      onSubmit();
    }
  };

  const handleMenuClick = (e) => {
    if (e.key === "view") {
      setResponsesModalOpen(true);
    } else if (e.key === "book") {
      goToBook(get(currentData, "data.chapter"), get(currentData, "data.page"));
    } else if (e.key === "start") {
      checkRetake();
    }
  };

  useMemo(() => {
    const getHiddenBooks =
      get(currentClass, "hide_books", []).map((data) => data?.bookId) || [];

    let filterAssignmentNew = filteredAssignments?.filter((assignment) => {
      const dueDate = dayjs(assignment.dueDate);
      const isVisible = !(
        get(currentUser, "role") === "student" &&
        getHiddenBooks.includes(assignment?.book?.id)
      );
      if (!isVisible) return false;
      if (dateRangeMode === "semester") return true;
      if (dateRangeMode === "current_month")
        return dueDate.isSame(currentMonth, "month");
      if (dateRangeMode === "last_month")
        return dueDate.isSame(dayjs().subtract(1, "month"), "month");
      if (dateRangeMode === "current_week")
        return dueDate.isSame(dayjs(), "week");
      if (dateRangeMode === "last_week")
        return dueDate.isSame(dayjs().subtract(1, "week"), "week");
      if (dateRangeMode === "last_30_days") {
        const start = dayjs().subtract(30, "day").startOf("day");
        const end = dayjs().endOf("day");
        const afterOrSameStart =
          dueDate.isAfter(start) || dueDate.isSame(start, "day");
        const beforeOrSameEnd =
          dueDate.isBefore(end) || dueDate.isSame(end, "day");
        return afterOrSameStart && beforeOrSameEnd;
      }
      if (dateRangeMode === "custom" && customStartDate && customEndDate) {
        const start = dayjs(customStartDate).startOf("day");
        const end = dayjs(customEndDate).endOf("day");
        const afterOrSameStart =
          dueDate.isAfter(start) || dueDate.isSame(start, "day");
        const beforeOrSameEnd =
          dueDate.isBefore(end) || dueDate.isSame(end, "day");
        return afterOrSameStart && beforeOrSameEnd;
      }
      return false;
    });
    if(viewType == 'quiz') {
      filterAssignmentNew = filterAssignmentNew?.filter((x) => x?.data?.is_exam == true);
    } else if(viewType == 'exam') {
      filterAssignmentNew = filterAssignmentNew?.filter((x) => x?.data?.is_unit_exam == true);
    }

    const groupByDate = filterAssignmentNew.reduce((acc, item) => {
      const dateKey = dayjs(item.dueDate).format("YYYY-MM-DD");
      if (!acc[dateKey]) acc[dateKey] = [];
      acc[dateKey].push(item);
      return acc;
    }, {});
    setGroupedByDate(groupByDate);
  }, [filteredAssignments, currentClass, currentMonth, dateRangeMode, customStartDate, customEndDate, viewType]);

  const getNumberMask = useCallback(
    (_page, chapter) => {
      let _book = find(booksAllList, { id: chapter });
      if (_book) {
        let numberMask = find(_book.pages, (k) => k.pageId == _page);
        if (numberMask) {
          if (numberMask.NumberMasked) return numberMask.NumberMasked;
          else return _page;
        } else {
          return _page;
        }
      } else {
        return _page;
      }
    },
    [booksAllList]
  );

  const getAssignmentName = (data, chapter, is_data_debate) => {
    let displayName = data?.displayName
      ? data?.displayName
      : data?.data?.displayName;
    let shortName = BookSynoname[chapter];
    let shortN = data?.typeId === "quiz" ? "Q" : "A";
    let pg = getNumberMask(
      data?.data?.page || data?.assignment?.pageId || data?.pageId,
      chapter
    );
    pg =
      pg?.length > 20 ? data?.data?.bookId?.substring(0, 10) : `Pg.${pg ?? ""}`;
    return is_data_debate
      ? displayName
      : `${displayName} (${shortName ?? shortN}|${pg || ""})`;
  };

  const isAssignmentTakable = useCallback(
    (data) => {
      if (get(data, "lateDate") == "always") {
        return true;
      } else if (get(data, "lateDate")) {
        if (isAfterTime(get(data, "lateDate"), servertime, timezone)) {
          return true;
        } else if (isAfterTime(get(data, "dueDate"), servertime, timezone)) {
          return true;
        }
      } else {
        if (isAfterTime(get(data, "dueDate"), servertime, timezone)) {
          return true;
        }
        return false;
      }
    },
    [servertime, groupedByDate]
  );

  const getCompleteInclompleteMark = (isComplete) => {
    if (isComplete) {
      return <Check style={{ color: COLOR.green100, fontSize: 18 }} />;
    } else {
      return <Close style={{ color: COLOR.red, fontSize: 18 }} />;
    }
  };
  const assignmentInfoData = (worksheetInfo) => {
    if (
      worksheetInfo &&
      worksheetInfo.id &&
      Array.isArray(class_game_activity) &&
      class_game_activity.length > 0
    ) {
      const activity = class_game_activity.find(
        (k) => k.game_id == worksheetInfo.id && k.classId == currentClass.id
      );
      if (activity) return activity;
      return {};
    }
    return {};
  };

  const resolveWorksheetSubmission = (data, submission) => {
    if (submission && (submission.assignmentId || submission.id)) {
      return submission;
    }
    if (data?.submission && (data.submission.assignmentId || data.submission.id)) {
      return data.submission;
    }
    return null;
  };

  const fetchCurrentSubmissionData = async (worksheetId, worksheetSubmission) => {
    try {
      if (!worksheetSubmission?.id) {
        const submitDataObj = {
          worksheet_id: worksheetId,
          answer: [],
          class_id: currentClass.id,
          student_id: currentUser.id,
          time_duration: 0,
          total_correct_answer: 0,
          total_wrong_answer: 0,
          finish_later: 0,
          complete: false,
          submissions: 0,
        };
        await dispatch(handleSubmitWorksheetAssignment(submitDataObj));
        dispatch(handleListUserSubmissionsByClass(currentUser?.id, currentClass?.id));
      }
    } catch (error) {
      Sentry.captureException(error);
    }
  };

  const graphicOrganizationRedirect = async (data, submission) => {
    const url = getEbookAssignmentDeepLinkUrl(data);
    if (url?.includes("worksheet")) {
      const worksheetId = data?.data?.id ?? data?.game_id;
      const isWorksheet =
        data?.type === "worksheet" || data?.data?.type === "worksheet";
      const hasSubmission = isWorksheet
        ? resolveWorksheetSubmission(data, submission)
        : null;
      const assignmentInfo = assignmentInfoData(data?.data);
      const scheduleAt = assignmentInfo?.scheduleAt;
      const dueDate = assignmentInfo?.dueDate;
      const now = dayjs();
      const isWithinSchedule =
        scheduleAt && dueDate
          ? now.isAfter(dayjs(scheduleAt)) && now.isBefore(dayjs(dueDate))
          : true;
      const is_practice_mode =
        hasSubmission?.complete || !isWithinSchedule ? true : false;
      const response = await dispatch(
        handleListQuestionOfWorksheet(worksheetId)
      );
      if (!is_practice_mode) {
        await fetchCurrentSubmissionData(worksheetId, hasSubmission);
      }
      const questionArray = [];
      if (Array.isArray(response)) {
        response.forEach((element) => {
          if (!element?.id) return;
          element.question.id = element?.question_id;
          questionArray.push({
            ...element?.question,
            image: element?.image,
            answer_audio: element?.answer_audio,
            question_audio: element?.question_audio,
          });
        });
      }
      const isResuming =
        hasSubmission &&
        hasSubmission.complete === false &&
        !is_practice_mode;
      const resumeData = isResuming
        ? {
            activeIndex: hasSubmission.answers?.length || 0,
            studentSubmission: hasSubmission.answers || [],
            isCorrect:
              hasSubmission.answers?.filter((a) => a.isCorrect)?.length || 0,
          }
        : null;
      const isAssigned = !!assignmentInfo?.id;
      const isActiveSchedule = !isAssigned || isWithinSchedule;
      const for_grade = isActiveSchedule && !hasSubmission?.complete;
      const chapter = data?.data?.chapter;
      const page = data?.data?.page;
      await dispatch(
        setWorksheetView({
          open: true,
          questions: questionArray,
          title: data?.data?.displayName,
          GetBookName: data?.data?.bookName,
          bookId: getNumberMask(page, chapter) || data?.bookId,
          assignmentInfo,
          for_grade,
          has_submission: is_practice_mode,
          resumeData,
        })
      );
    }
    if (url) {
      navigate(url);
      return true;
    }
    return false;
  };

  const reditrectToAssignment = async () => {
    const chapter = get(currentData, "data.chapter", "");
    const assignmentId = get(currentData, "data.id");
    const typeId = get(currentData, "data.typeId", "");
    const is_graphic_organizer =
      get(currentData, "data.type") === "graphic_organizer" ||
      get(currentData, "data.activity_type") === "graphic_organizer";
    const assignmentPage = get(currentData, "data.page", 0);
    const generatedUrl = generateUrl(
      chapter,
      assignmentPage,
      typeId.includes("activity") ? assignmentId : null,
      typeId.includes("quiz") ? assignmentId : null,
      is_graphic_organizer
    );
    if (isVideo(chapter, resources)) {
      navigate(getAssignmentNavigationUrl(`/video?id=${chapter}&quiz=${assignmentId}`, currentData));
    } else if (generatedUrl) {
      navigate(getAssignmentNavigationUrl(generatedUrl, currentData));
    } else {
      if (!(await graphicOrganizationRedirect(currentData, currentData?.submission))) {
        navigate(
          `/ebook?chapter=${chapter}&page=${assignmentPage}&activity=${
            typeId.includes("activity") ? assignmentId : null
          }&quiz=${typeId.includes("quiz") ? assignmentId : null}`
        );
      }
    }
  };

  const onSubmit = async () => {
    const chapter = get(currentData, "data.chapter", "");
    if (isExamHidden(chapter)) {
      showAlert("Your teacher has hidden this exam and you are no longer able to take it.", "info");
      return;
    }
    // if (!isActive || deactivateStudent) {
    //   return;
    // }
    if (currentData?.submission?.complete && !retaksAvailable) {
      return;
    } else if (currentData?.submission?.complete && retaksAvailable) {
      await dispatch(
        handleCreatePastSubmission({
          submissionId: currentData?.submission?.id,
          is_missing: currentData?.submission?.exceptionStatus ==3?true: false
        })
      );
      await reditrectToAssignment();
    } else {
      await reditrectToAssignment();
    }
  };
  const getGrade = (
    data,
    submission,
    max_point,
    ungraded,
    isPointSystem,
    isCompleteInComplete
  ) => {
    if (!submission || !submission.id) return "-";
    if (submission.retaking && !submission.complete) {
        return "-";
    }
    const grade = submission?.grade >= 0 ? submission.grade : "";
    const aiGradeExists = submission && submission.ai_grade && submission.ai_grade?.length > 0;
    let bg_text;
    if (currentUser?.role === 'student') {
      bg_text = 'TBG';
    } else {
      bg_text = aiGradeExists ? 'TBG - AI' : 'TBG';
    }
    const face_grade = submission?.face_grade;
    const isTbg =
      submission?.manualGrading &&
      checkIsManualGradable(get(submission, "assignment.questions"));
      if(submission?.assignmentType === "bingo" && ungraded){
        const isSubmitted = submission?.answers?.length > 0 ? submission?.answers?.some((el)=> el?.is_submitted):false;
        return isSubmitted ? 'Credit' : '-';
      }
    if (ungraded) return "Credit";
    if (
      isCompleteInComplete &&
      (has(submission, "id") || !isAssignmentTakable(data))
    ) {
      return getCompleteInclompleteMark(submission?.grade > 0);
    }
    if (isPointSystem) {
      if (isTbg) {
        return face_grade
          ? `${face_grade}/${max_point} (${bg_text})`
          : `${bg_text}/${max_point}`;
      }
      return face_grade > 0 || face_grade == 0
        ? `${face_grade}/${max_point}`
        : `0/${max_point}`;
    } else {
      if (isTbg) {
        return grade == 0 || grade > 0 ? `${grade} (${bg_text})` : `${bg_text}`;
      }
      return grade == 0 || grade > 0 ? grade : "-";
    }
  };

  const goToPreviousMonth = () => {
    setCurrentMonth((prev) => prev.subtract(1, "month"));
  };

  const goToNextMonth = () => {
    setCurrentMonth((prev) => prev.add(1, "month"));
  };

  const getDateRangeLabel = () => {
    if (dateRangeMode === "semester") return "Current semester";
    if (dateRangeMode === "current_month")
      return dayjs(currentMonth).format("MMMM YYYY");
    if (dateRangeMode === "last_month")
      return dayjs().subtract(1, "month").format("MMMM YYYY");
    if (dateRangeMode === "current_week") return "Current week";
    if (dateRangeMode === "last_week") return "Last week";
    if (dateRangeMode === "last_30_days") return "Last 30 days";
    if (dateRangeMode === "custom" && customStartDate && customEndDate)
      return `${dayjs(customStartDate).format("MMM D, YYYY")} – ${dayjs(customEndDate).format("MMM D, YYYY")}`;
    return "Current semester";
  };

  const dateRangeDropdownContent = (
    <div
      style={{
        background: "#fff",
        borderRadius: 8,
        boxShadow: "0 6px 16px rgba(0,0,0,0.08)",
        minWidth: 220,
        padding: "8px 0",
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <div
        style={{
          padding: "8px 12px",
          cursor: "pointer",
          background: dateRangeMode === "semester" ? "#f0f0f0" : "transparent",
        }}
        onClick={() => {
          setDateRangeMode("semester");
          setDateDropdownOpen(false);
        }}
      >
        Current semester
      </div>
      <div
        style={{
          padding: "8px 12px",
          cursor: "pointer",
          background: dateRangeMode === "current_month" ? "#f0f0f0" : "transparent",
        }}
        onClick={() => {
          setDateRangeMode("current_month");
          setCurrentMonth(dayjs().startOf("month"));
          setDateDropdownOpen(false);
        }}
      >
        Current month
      </div>
      <div
        style={{
          padding: "8px 12px",
          cursor: "pointer",
          background: dateRangeMode === "last_month" ? "#f0f0f0" : "transparent",
        }}
        onClick={() => {
          setDateRangeMode("last_month");
          setDateDropdownOpen(false);
        }}
      >
        Last month
      </div>
      <div
        style={{
          padding: "8px 12px",
          cursor: "pointer",
          background: dateRangeMode === "current_week" ? "#f0f0f0" : "transparent",
        }}
        onClick={() => {
          setDateRangeMode("current_week");
          setDateDropdownOpen(false);
        }}
      >
        Current week
      </div>
      <div
        style={{
          padding: "8px 12px",
          cursor: "pointer",
          background: dateRangeMode === "last_week" ? "#f0f0f0" : "transparent",
        }}
        onClick={() => {
          setDateRangeMode("last_week");
          setDateDropdownOpen(false);
        }}
      >
        Last week
      </div>
      <div
        style={{
          padding: "8px 12px",
          cursor: "pointer",
          background: dateRangeMode === "last_30_days" ? "#f0f0f0" : "transparent",
        }}
        onClick={() => {
          setDateRangeMode("last_30_days");
          setDateDropdownOpen(false);
        }}
      >
        Last 30 days
      </div>
      <div style={{ padding: "12px", borderTop: "1px solid #f0f0f0" }}>
        <div style={{ fontSize: 12, marginBottom: 8, color: "#666" }}>
          Custom range
        </div>
        <DatePicker
          placeholder="Start date"
          value={customStartDate ? dayjs(customStartDate) : null}
          onChange={(d) => setCustomStartDate(d ? d.toDate() : null)}
          style={{ width: "100%", marginBottom: 8 }}
          getPopupContainer={(trigger) => trigger.parentNode}
        />
        <DatePicker
          placeholder="End date"
          value={customEndDate ? dayjs(customEndDate) : null}
          onChange={(d) => setCustomEndDate(d ? d.toDate() : null)}
          style={{ width: "100%", marginBottom: 8 }}
          getPopupContainer={(trigger) => trigger.parentNode}
        />
        <button
          type="button"
          style={{
            width: "100%",
            padding: "6px 12px",
            cursor: "pointer",
            background: "#1890ff",
            color: "#fff",
            border: "none",
            borderRadius: 4,
          }}
          onClick={() => {
            if (customStartDate && customEndDate) {
              setDateRangeMode("custom");
              setDateDropdownOpen(false);
            }
          }}
        >
          Apply
        </button>
      </div>
    </div>
  );

  const isDoneLate = (createdAt, dueDate, lateDate) => {
    if (!lateDate) return false;

    const createdAtTime = Number(createdAt);
    const dueDateTime = new Date(dueDate).getTime();
    const lateDateTime = new Date(lateDate).getTime();

    return createdAtTime > dueDateTime && createdAtTime <= lateDateTime;
  };

  const getButtonText = useCallback(
    (submission, item) => {
      if (item?.type === "worksheet" && submission?.complete) return;
      const retakeAvailable =  submission
        ? submission?.exceptionStatus ==3?true: parseInt(submission?.submissions, 10) <
          parseInt(item?.retakes, 10) + 1
        : true;
      if (isAssignmentTakable(item)) {
        if (submission?.complete && retaksAvailable) {
          return "Retake";
        } else if (submission) {
          return "Resume";
        } else {
          return "Start";
        }
      }
    },
    [servertime, groupedByDate, retaksAvailable]
  );

  const menu = useMemo(() => {
    const items = [
      {
        key: 'view',
        label: 'View Response',
        disabled:
          !currentData?.submission ||
          (!view_result) ||
          (currentData?.type !== "worksheet" &&
            currentData?.submission?.submissions === 0 &&
            !(currentData?.submission?.pastSubmissions?.length > 0))
      }
    ];

    if (currentUser?.role !== 'parent') {
      if ((currentData?.type !== "game" && currentData?.type !== "debate")) {
        items.push({
          key: 'book',
          label: 'Go to Book'
        });
      }
  
      if (currentData?.dueDate && isAssignmentTakable(currentData)) {
        items.push({
          key: 'start',
          label: getButtonText(currentData?.submission, currentData)
        });
      }
    }

    return {
      items,
      onClick: handleMenuClick
    };
  }, [currentData, isAssignmentTakable, getButtonText, handleMenuClick]);

  return (
    <SentryWrap>
      <Modal
        open={modalOpen}
        onClose={() => setModalState(false)}
        style={{ height: "100%", width: "100%" }}>
        <ImageModal>
         <div className="mdlBookTitleCstmUpr">  
           {getThumbnail(true)}
           {coverTitle ? <label className="mdlBookTitleCstm">{coverTitle}</label> : null}
         </div>
        </ImageModal>
      </Modal>

      <AssignmentResponse
        responsesModalOpen={responsesModalOpen}
        setResponsesModalOpen={setResponsesModalOpen}
        submission={currentData?.submission}
        data={currentData}
        getCompleteInclompleteMark={getCompleteInclompleteMark}
        isAssignmentTakable={isAssignmentTakable}
      />
      <RetakeModal
        isRetakeOpen={isRetakeOpen}
        onClose={() => setisRetakeOpen(false)}
        onSubmit={onSubmit}
      />
      <div className="calenderDateWrap">
        <div className="calenderDate">
          {dateRangeMode === "current_month" && (
            <img
              src={ArrowLeftSvg}
              alt=""
              onClick={goToPreviousMonth}
              style={{ cursor: "pointer" }}
            />
          )}
          <Dropdown
            open={dateDropdownOpen}
            onOpenChange={setDateDropdownOpen}
            dropdownRender={() => dateRangeDropdownContent}
            trigger={["click"]}
          >
            <div
              style={{
                cursor: "pointer",
                userSelect: "none",
                display: "inline-flex",
                alignItems: "center",
                gap: 4,
                padding: "6px 12px",
                border: "1px solid #d9d9d9",
                borderRadius: 6,
                background: "#fff",
                fontSize: 16,
                fontWeight: 600,
              }}
              onClick={() => setDateDropdownOpen(!dateDropdownOpen)}
            >
              <span>{getDateRangeLabel()}</span>
              <KeyboardArrowDown
                style={{
                  fontSize: 22,
                  transform: dateDropdownOpen ? "rotate(180deg)" : "none",
                  transition: "transform 0.2s",
                }}
              />
            </div>
          </Dropdown>
          {dateRangeMode === "current_month" && (
            <img
              src={ArrowRightSvg}
              alt=""
              onClick={goToNextMonth}
              style={{ cursor: "pointer" }}
            />
          )}
        </div>
        {(!groupedByDate || Object.keys(groupedByDate).length === 0) && (
          <div className="noAssignImg">
            <NoAssignmentImg src={nothingToSee} />
          </div>
        )}
        <div className="ebookCalWraper">
          {(() => {
            const sortedEntries = Object.entries(groupedByDate).sort(([a], [b]) => 
              sectionSort === 'newest_to_oldest' ? new Date(b) - new Date(a) : new Date(a) - new Date(b)
            );
            
            const paddedEntries = sortedEntries.length < 5 
              ? [
                  ...sortedEntries,
                  ...Array(5 - sortedEntries.length).fill(null).map((_, idx) => [`empty-${idx}`, []])
                ]
              : sortedEntries;
            
            return paddedEntries.map(([dateKey, items]) => {
              const maxLength = Math.max(
                ...Object.values(groupedByDate).map((list) => list.length),
                0
              );
            const paddedItems = [
              ...items,
              ...Array(maxLength - items.length).fill(null),
            ];
            return (
              <div className="ebookCalInfoMain" key={dateKey}>
                {paddedItems.map((item, idx) => {
                  if (!item || item==null) {
                    return (
                      <div className="ebookCalInfoUpr ebookCalInfoBlankUpr" key={idx}>
                        <div className="ebookCalInfo">
                          <AssignmentTitle title={"-"} description={true} />
                          <AssignmentTitle title={"-"} />
                          <span className="setBlueHypen">{"-"}</span>
                        </div>
                        <div className="ebookValInfoUpr">
                          <div className="ebookValInfo blankBox">
                            <div className="ebookInerInfo">
                              <img
                                src={eyeIconSvg}
                                alt=""
                              />
                            </div>
                            <div className="ebookInerInfo">
                              <input
                                type="text"
                                value={"-"}
                                readOnly
                              />
                            </div>
                            <div className="ebookInerInfo">
                              <img
                                src={dotsSvg}
                                alt=""
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  }

                  const is_data_debate = item.type === "debate";
                  const grade_type = get(item, "grade_type");
                  const isPointSystem = grade_type === "points";
                  const isCompleteInComplete =
                    grade_type === "complete_incomplete";
                  const max_point =
                    item.max_point && !isNaN(item.max_point)
                      ? Number.parseInt(item.max_point, 10)
                      : 100;
                  const ungraded = get(item, "ungraded", false);
                  const AssignmentTitleText = getAssignmentName(
                    item,
                    get(item, "data.chapter"),
                    is_data_debate
                  );
                  // const deactive_for_me =  useMemo(()=>isDisabledForMe(data,currentUser.id),[data,currentUser])
                  const isActive = get(item, "active", true);
                  const deactivateStudent = item?.deactivated_student?.includes(
                    currentUser?.id
                  );
                  const submission = fetchAssignmentSubmission(item);
                  const retakeAvailable = submission
                    ? submission?.exceptionStatus ==3?true: parseInt(submission?.submissions, 10) <
                      parseInt(item?.retakes, 10) + 1
                    : true;
                  return (
                    <div className="ebookCalInfoUpr" key={idx}>
                      <div className="ebookCalInfo">
                        <AssignmentTitle
                          title={get(item, "book.description", "-")}
                          description={true}
                          is_data_debate={is_data_debate}
                        />
                        <AssignmentTitle title={AssignmentTitleText} />
                        <span>
                          {dayjs(item.dueDate).format("MMM DD").toUpperCase()}
                        </span>
                      </div>
                      <div className="ebookValInfoUpr">
                        <div className="ebookValInfo">
                          <div
                            className="ebookInerInfo"
                            style={{ cursor: "pointer" }}
                            onClick={() => {
                              setCurrentData(item);
                              setModalState(true);
                            }}
                          >
                            <img
                              src={eyeIconSvg}
                              alt=""
                            />
                          </div>
                          {/*<div className="ebookInerInfo">
                            <input
                              type="text"
                              value={
                                getGrade(
                                  item,
                                  submission,
                                  max_point,
                                  ungraded,
                                  isPointSystem,
                                  isCompleteInComplete
                                )
                                  ?.toString()
                                  .split("/")[0] // Only show grade, not `/max_point`
                              }
                              readOnly
                            />
                          </div>
                          {isPointSystem && (
                            <p
                              style={{
                                margin: 0,
                                fontWeight: "700",
                                fontSize: "15px",
                              }}
                            >
                              / {max_point}
                            </p>
                          )} */}
                          {(() => {
                            const fullGrade = getGrade(
                              item,
                              submission,
                              max_point,
                              ungraded,
                              isPointSystem,
                              isCompleteInComplete
                            )?.toString();

                            const [gradeValueWithText, rightPart] =
                              fullGrade.split("/");
                            const gradeOnly = gradeValueWithText
                              ?.split("(")[0]
                              ?.trim(); // "7" from "7 (TBG)"
                            const bgTextMatch =
                              gradeValueWithText?.match(/\((.*?)\)/);
                            const bgText = bgTextMatch ? bgTextMatch[1] : null;

                            return (
                              <>
                                <div className="ebookInerInfo">
                                  <input
                                    type="text"
                                    value={gradeOnly || "-"}
                                    readOnly
                                  />
                                  {bgText && (
                                    <label className="tgbLabel">
                                      {bgText}
                                    </label>
                                  )}
                                </div>

                                {isPointSystem && (
                                  <p
                                    style={{
                                      margin: 0,
                                      fontWeight: "700",
                                      fontSize: "15px",
                                    }}
                                  >
                                    / {max_point}
                                  </p>
                                )}
                              </>
                            );
                          })()}

                          <Dropdown menu={menu} trigger={["click"]}>
                            <div
                              className="ebookInerInfo"
                              style={{ cursor: "pointer" }}
                              onClick={() => {
                                setCurrentData(item);
                                setRetakesAvailable(retakeAvailable);
                              }}
                            >
                              <img
                                src={dotsSvg}
                                alt=""
                              />
                            </div>
                          </Dropdown>
                        </div>

                        {isDoneLate(
                          submission?.createdAt,
                          item?.dueDate,
                          item?.lateDate
                        ) && (
                          <div className="valStatus valDoneLate">
                            <p>{"Done Late"}</p>
                          </div>
                        )}

                        {item.dueDate && (
                          <>
                            {!get(submission, "complete") &&
                            !isAssignmentTakable(item) ? (
                              <div className="valStatus">
                                <p>{"Past Due"}</p>
                              </div>
                            ) : (
                              <>
                                {!isDoneLate(
                                  submission?.createdAt,
                                  item?.dueDate,
                                  item?.lateDate
                                ) && (
                                  <div className="valStatus">
                                    {get(submission, "complete") &&
                                      (!retakeAvailable ||
                                        (isBeforTime(
                                          get(item, "dueDate"),
                                          servertime,
                                          timezone
                                        ) &&
                                          isBeforTime(
                                            get(
                                              item,
                                              "lateDate",
                                              get(item, "dueDate")
                                            ),
                                            servertime,
                                            timezone
                                          ))) && (
                                        <CompleteText
                                          className="ebookStatusSpan"
                                          deactivate={
                                            !isActive || deactivateStudent
                                          }
                                        >
                                          {isActive ? (
                                            <img
                                              src={completeGradebookSvg}
                                              alt=""
                                            />
                                          ) : null}
                                          {!isActive || deactivateStudent
                                            ? "Deactivated"
                                            : "Completed"}
                                        </CompleteText>
                                      )}
                                  </div>
                                )}
                              </>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            );
            });
          })()}
        </div>
      </div>
    </SentryWrap>
  );
}
